import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Camera, Mic, MapPin, StickyNote, Sparkles, FileText, Pause, CalendarCheck,
  FlagOff, RefreshCw, WifiOff, Wifi, Bell, Plus, CheckCircle2, Clock, AlertTriangle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import * as api from "../lib/reporterApi";
import * as Q from "../lib/reporterQueue";
import { flushQueue, installAutoSync } from "../lib/reporterSync";

const EDITORIAL = ["editor", "admin", "super_admin"];

function Section({ title, children, testid }) {
  return (
    <div className="rounded-2xl bg-white/80 border border-slate-200 p-4 shadow-sm" data-testid={testid}>
      {title && <h3 className="text-sm font-semibold text-slate-700 mb-3">{title}</h3>}
      {children}
    </div>
  );
}

export default function ReporterPage() {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState(null);
  const [days, setDays] = useState([]);
  const [activeDay, setActiveDay] = useState(null);
  const [captures, setCaptures] = useState([]);
  const [queue, setQueue] = useState([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [quota, setQuota] = useState({ ratio: 0, pressure: false });
  const [notifs, setNotifs] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [locLabel, setLocLabel] = useState("");
  const [newSessionTitle, setNewSessionTitle] = useState("");
  const [recording, setRecording] = useState(false);
  const [swReady, setSwReady] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);

  const canUse = isAuthenticated && EDITORIAL.includes(user?.role);

  // ---- queue subscription ----
  const refreshQueue = useCallback(async () => {
    setQueue(await Q.all());
    setQuota(await Q.quota());
  }, []);

  useEffect(() => {
    const unsub = Q.subscribe(refreshQueue);
    refreshQueue();
    return () => unsub();
  }, [refreshQueue]);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // ---- data loading ----
  const loadSessions = useCallback(async () => {
    try {
      const res = await api.listReporterSessions();
      setSessions(res.data || []);
      // Phase D2 édito — priorité à l'URL param `?session=` (ouverture depuis un voyage).
      const params = new URLSearchParams(window.location.search);
      const requested = params.get("session");
      if (requested && (res.data || []).some((s) => s.id === requested)) {
        if (!session || session.id !== requested) selectSession(requested);
        return;
      }
      if (!session && res.data && res.data.length) selectSession(res.data[0].id);
    } catch (_e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const selectSession = useCallback(async (id) => {
    try {
      const res = await api.getReporterSession(id);
      setSession(res.data.session);
      setDays(res.data.days || []);
      setActiveDay(res.data.active_day || null);
      const caps = await api.listReporterCaptures(id, res.data.active_day?.id);
      setCaptures(caps.data || []);
      const sug = await api.getDaySuggestions(id);
      setSuggestions(sug.data || []);
    } catch (_e) {}
  }, []);

  const loadNotifs = useCallback(async () => {
    try {
      const res = await api.getReporterNotifications();
      setNotifs(res.data || []);
    } catch (_e) {}
  }, []);

  useEffect(() => {
    if (!canUse) return;
    loadSessions();
    loadNotifs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUse]);

  // ---- auto-sync ----
  useEffect(() => {
    if (!session) return;
    const stop = installAutoSync(() => session?.id);
    return stop;
  }, [session]);

  const doFlush = useCallback(async () => {
    if (!session) return;
    await flushQueue(session.id);
    await refreshQueue();
    await selectSession(session.id);
  }, [session, refreshQueue, selectSession]);

  // ---- actions ----
  const createSession = async () => {
    const res = await api.createReporterSession({
      title: newSessionTitle || "Nouveau reportage",
      working_title: newSessionTitle || "Nouveau reportage",
      trip_type: "croisiere",
    });
    setNewSessionTitle("");
    await loadSessions();
    await selectSession(res.data.id);
    toast.success("Reportage créé");
  };

  const addDay = async () => {
    if (!session) return;
    const dayNumber = (days.length || 0) + 1;
    const today = new Date().toISOString().slice(0, 10);
    await api.createReporterDay(session.id, {
      day_number: dayNumber,
      calendar_date: today,
      title: `Jour ${dayNumber}`,
    });
    await selectSession(session.id);
    toast.success(`Jour ${dayNumber} ajouté`);
  };

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      await Q.enqueue({
        kind: "photo",
        blob: f,
        payload: { capture_timestamp: new Date(f.lastModified || Date.now()).toISOString() },
      });
    }
    e.target.value = "";
    toast.success(`${files.length} photo(s) en file`);
    doFlush();
  };

  const addNote = async (type) => {
    if (!noteText.trim() || !activeDay) {
      toast.error("Sélectionnez un jour et saisissez du texte");
      return;
    }
    await Q.enqueue({ kind: type, payload: { text: noteText.trim(), day_id: activeDay.id } });
    setNoteText("");
    toast.success(type === "impression" ? "Impression enregistrée" : "Note enregistrée");
    doFlush();
  };

  const toggleRecord = async () => {
    if (recording) {
      mediaRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => ev.data.size > 0 && chunksRef.current.push(ev.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await Q.enqueue({ kind: "voice", blob, state: "TRANSCRIPTION_PENDING", payload: { day_id: activeDay?.id } });
        setRecording(false);
        toast.success("Note vocale en file (transcription à la sync)");
        doFlush();
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (_e) {
      toast.error("Micro indisponible");
    }
  };

  const useGps = async () => {
    if (!navigator.geolocation) {
      toast.error("GPS non supporté");
      return;
    }
    // Explicit action only — never requested on load.
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        await api.createLocationEvent(session.id, {
          location_source: "DEVICE_GPS",
          label: locLabel || undefined,
          day_id: activeDay?.id,
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        toast.success("Position enregistrée (coordonnées brutes réservées au super-admin)");
      },
      () => toast.error("Permission GPS refusée"),
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  const setManualLocation = async () => {
    if (!locLabel.trim()) return;
    await api.createLocationEvent(session.id, {
      location_source: "MANUAL",
      label: locLabel.trim(),
      day_id: activeDay?.id,
    });
    setLocLabel("");
    await selectSession(session.id);
    toast.success("Lieu mis à jour");
  };

  const analyzeNow = async () => {
    toast.info("Analyse Blue (batch)…");
    try {
      const res = await api.analyzeSession(session.id);
      toast.success(res.data?.analysis ? "Analyse prête" : "Analyse indisponible");
      loadNotifs();
    } catch (_e) {
      toast.error("Analyse impossible");
    }
  };

  const openArticle = async () => {
    try {
      const res = await api.buildArticle(session.id);
      toast.success(`Article enrichi (${res.data.block_count} blocs)`);
      if (res.data.article_id) navigate(`/article/${res.data.article_id}/edit`);
    } catch (_e) {
      toast.error("Construction article impossible");
    }
  };

  const acceptSuggestion = async (s) => {
    for (const cid of s.capture_ids) {
      await api.assignCaptureDay(cid, s.day_id);
    }
    await selectSession(session.id);
    toast.success(`${s.count} photo(s) assignée(s)`);
  };

  const enableFieldMode = async () => {
    if (!("serviceWorker" in navigator)) {
      toast.error("PWA non supportée sur ce navigateur (mode in-app conservé)");
      return;
    }
    try {
      await navigator.serviceWorker.register("/reporter-sw.js", { scope: "/reporter" });
      setSwReady(true);
      toast.success("Mode terrain activé (capture hors-ligne)");
    } catch (_e) {
      toast.error("Activation impossible");
    }
  };

  const enableNotifications = async () => {
    if (!("Notification" in window)) {
      toast.error("Notifications non supportées (in-app conservées)");
      return;
    }
    // Explicit action only — never requested on first visit.
    const perm = await Notification.requestPermission();
    toast[perm === "granted" ? "success" : "info"](
      perm === "granted" ? "Notifications activées" : "Notifications refusées (in-app conservées)"
    );
  };

  // ---- render guards ----
  if (loading) return <div className="min-h-screen flex items-center justify-center">Chargement…</div>;
  if (!canUse) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center" data-testid="reporter-denied">
        <p className="text-slate-600">Espace réservé aux utilisateurs éditoriaux (editor / admin / super_admin).</p>
        <Button onClick={() => navigate("/connexion")} data-testid="reporter-login-btn">Se connecter</Button>
      </div>
    );
  }

  const pendingCount = queue.filter((q) => q.state !== "DONE").length;
  const stateLabel = (s) =>
    ({ SYNC_PENDING: "Sync…", TRANSCRIPTION_PENDING: "Transcription…", ANALYSIS_PENDING: "Analyse…", DONE: "OK" }[s] || s);

  return (
    <div className="min-h-screen bg-slate-100 pb-24" data-testid="reporter-page">
      {/* Header + active day banner */}
      <div className="sticky top-0 z-20 bg-[#0f2b46] text-white px-4 py-3 shadow-md">
        <div className="flex items-center justify-between">
          <div className="font-semibold tracking-wide">ITERCATUS · Reporter</div>
          <div className="flex items-center gap-2 text-xs">
            {online ? <Wifi className="h-4 w-4 text-emerald-300" /> : <WifiOff className="h-4 w-4 text-amber-300" />}
            <span data-testid="reporter-online-state">{online ? "En ligne" : "Hors ligne"}</span>
          </div>
        </div>
        {session && (
          <div className="mt-2 text-lg font-bold uppercase" data-testid="active-day-banner">
            {(session.working_title || session.title || "Reportage")}
            {activeDay ? ` — ${activeDay.title || "Jour " + activeDay.day_number}` : " — Aucun jour actif"}
          </div>
        )}
      </div>

      <div className="p-4 space-y-4 max-w-2xl mx-auto">
        {/* Session picker / create */}
        <Section title="Reportage actif" testid="reporter-session-section">
          <div className="flex flex-wrap gap-2 mb-3">
            {sessions.map((s) => (
              <button
                key={s.id}
                onClick={() => selectSession(s.id)}
                className={`px-3 py-1.5 rounded-full text-sm ${session?.id === s.id ? "bg-[#0f2b46] text-white" : "bg-slate-200 text-slate-700"}`}
                data-testid={`session-chip-${s.id}`}
              >
                {s.working_title || s.title} · {s.status}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newSessionTitle} onChange={(e) => setNewSessionTitle(e.target.value)}
              placeholder="Titre du reportage" data-testid="new-session-input" />
            <Button onClick={createSession} data-testid="create-session-btn"><Plus className="h-4 w-4" /></Button>
          </div>
        </Section>

        {session && (
          <>
            {/* Days */}
            <Section title="Jours" testid="reporter-days-section">
              <div className="flex flex-wrap gap-2">
                {days.map((d) => (
                  <button key={d.id} onClick={() => api.activateReporterDay(session.id, d.id).then(() => selectSession(session.id))}
                    className={`px-3 py-1.5 rounded-lg text-sm ${activeDay?.id === d.id ? "bg-amber-400 text-slate-900 font-semibold" : "bg-slate-200 text-slate-700"}`}
                    data-testid={`day-chip-${d.id}`}>
                    {d.title || `Jour ${d.day_number}`}
                  </button>
                ))}
                <Button variant="outline" size="sm" onClick={addDay} data-testid="add-day-btn"><Plus className="h-4 w-4 mr-1" />Jour</Button>
              </div>
            </Section>

            {/* Capture actions */}
            <Section title="Capture terrain" testid="reporter-capture-section">
              <div className="grid grid-cols-2 gap-3">
                <input ref={fileInputRef} type="file" accept="image/*" capture="environment" multiple hidden onChange={onPhotos} data-testid="photo-input" />
                <Button className="h-16 flex-col gap-1" onClick={() => fileInputRef.current?.click()} data-testid="capture-photo-btn">
                  <Camera className="h-5 w-5" /> Photo
                </Button>
                <Button className="h-16 flex-col gap-1" variant={recording ? "destructive" : "default"} onClick={toggleRecord} data-testid="capture-voice-btn">
                  <Mic className="h-5 w-5" /> {recording ? "Arrêter" : "Note vocale"}
                </Button>
              </div>
              <Textarea className="mt-3" rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)}
                placeholder="Note ou impression…" data-testid="note-textarea" />
              <div className="grid grid-cols-2 gap-3 mt-2">
                <Button variant="outline" onClick={() => addNote("note")} data-testid="add-note-btn"><StickyNote className="h-4 w-4 mr-1" />Note</Button>
                <Button variant="outline" onClick={() => addNote("impression")} data-testid="add-impression-btn"><Sparkles className="h-4 w-4 mr-1" />Impression</Button>
              </div>
              <div className="flex gap-2 mt-3">
                <Input value={locLabel} onChange={(e) => setLocLabel(e.target.value)} placeholder="Lieu (navire, port, ville…)" data-testid="location-input" />
                <Button variant="outline" onClick={setManualLocation} data-testid="set-location-btn"><MapPin className="h-4 w-4" /></Button>
                <Button variant="outline" onClick={useGps} data-testid="use-gps-btn">GPS</Button>
              </div>
            </Section>

            {/* Day suggestions (EXIF) */}
            {suggestions.length > 0 && (
              <Section title="Suggestions d'assignation" testid="reporter-suggestions-section">
                {suggestions.map((s) => (
                  <div key={s.day_id} className="flex items-center justify-between text-sm py-1">
                    <span>Ces {s.count} photo(s) semblent appartenir à un autre jour.</span>
                    <Button size="sm" onClick={() => acceptSuggestion(s)} data-testid={`accept-suggestion-${s.day_id}`}>Assigner</Button>
                  </div>
                ))}
              </Section>
            )}

            {/* Offline queue */}
            <Section title={`File hors-ligne (${pendingCount} en attente)`} testid="reporter-queue-section">
              {quota.pressure && (
                <div className="flex items-center gap-2 text-amber-700 text-sm mb-2" data-testid="quota-warning">
                  <AlertTriangle className="h-4 w-4" /> Stockage local proche de la limite — synchronisez.
                </div>
              )}
              <div className="space-y-1 max-h-40 overflow-auto">
                {queue.filter((q) => q.state !== "DONE").map((q) => (
                  <div key={q.client_id} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3 text-slate-400" /> {q.kind} · {stateLabel(q.state)}
                      {q.attempts > 0 && <span className="text-amber-600"> (retry {q.attempts})</span>}
                    </span>
                  </div>
                ))}
                {pendingCount === 0 && <div className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> File vide — tout est synchronisé.</div>}
              </div>
              <Button variant="outline" size="sm" className="mt-2" onClick={doFlush} data-testid="retry-now-btn"><RefreshCw className="h-4 w-4 mr-1" />Réessayer maintenant</Button>
            </Section>

            {/* Blue + article */}
            <Section title="Blue Field Assistant" testid="reporter-blue-section">
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" onClick={analyzeNow} data-testid="analyze-now-btn"><Sparkles className="h-4 w-4 mr-1" />Analyser</Button>
                <Button variant="outline" onClick={openArticle} data-testid="open-article-btn"><FileText className="h-4 w-4 mr-1" />Article évolutif</Button>
              </div>
            </Section>

            {/* Session controls */}
            <Section title="Contrôles" testid="reporter-controls-section">
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" size="sm" onClick={() => api.pauseReporterSession(session.id).then(() => selectSession(session.id))} data-testid="pause-btn"><Pause className="h-4 w-4 mr-1" />Pause</Button>
                <Button variant="outline" size="sm" onClick={() => activeDay && api.closeReporterDay(session.id, activeDay.id).then(() => { toast.success("Jour clôturé"); loadNotifs(); })} data-testid="close-day-btn"><CalendarCheck className="h-4 w-4 mr-1" />Fin jour</Button>
                <Button variant="outline" size="sm" onClick={() => api.completeReporterSession(session.id).then(() => selectSession(session.id))} data-testid="end-trip-btn"><FlagOff className="h-4 w-4 mr-1" />Fin voyage</Button>
              </div>
            </Section>

            {/* Recent captures */}
            <Section title={`Captures récentes (${captures.length})`} testid="reporter-recent-section">
              <div className="grid grid-cols-4 gap-2">
                {captures.slice(0, 12).map((c) => (
                  <div key={c.id} className="aspect-square rounded-lg overflow-hidden bg-slate-200" data-testid={`capture-thumb-${c.id}`}>
                    <MediaThumb mediaId={c.media_id} />
                  </div>
                ))}
              </div>
            </Section>
          </>
        )}

        {/* Notifications + PWA */}
        <Section title="Notifications & mode terrain" testid="reporter-notifs-section">
          <div className="space-y-1 mb-3 max-h-40 overflow-auto">
            {notifs.slice(0, 6).map((n) => (
              <div key={n.id} className="flex items-start gap-2 text-xs bg-slate-50 rounded px-2 py-1" data-testid={`notif-${n.id}`}>
                <Bell className="h-3 w-3 mt-0.5 text-[#0f2b46]" />
                <div><div className="font-medium">{n.title}</div><div className="text-slate-500">{n.body}</div></div>
              </div>
            ))}
            {notifs.length === 0 && <div className="text-xs text-slate-400">Aucune notification.</div>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" size="sm" onClick={enableFieldMode} data-testid="enable-field-mode-btn">{swReady ? "Mode terrain actif" : "Activer mode terrain"}</Button>
            <Button variant="outline" size="sm" onClick={enableNotifications} data-testid="enable-push-btn">Activer notifications</Button>
          </div>
        </Section>
      </div>
    </div>
  );
}

function MediaThumb({ mediaId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!mediaId) return;
    import("../lib/api").then(({ getMedia }) =>
      getMedia(mediaId).then((r) => { if (alive) setUrl(r.data?.public_cloudinary_url); }).catch(() => {})
    );
    return () => { alive = false; };
  }, [mediaId]);
  if (!url) return <div className="w-full h-full animate-pulse bg-slate-300" />;
  return <img src={url} alt="" className="w-full h-full object-cover" />;
}
